import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { projectsApi } from '../../services/projectsApi.js';
import { formatDate, formatNumber } from '../../utils/formatters.js';

import { TimeEntryModal } from './TimeEntryModal.jsx';

const CSV_COLUMNS = [
  { key: 'entryDate', label: 'Date' },
  { key: 'employeeName', label: 'Employee' },
  { key: 'hours', label: 'Hours' },
  { key: 'billable', label: 'Billable', map: (r) => (r.billable ? 'yes' : 'no') },
  { key: 'hourlyRate', label: 'Rate' },
  { key: 'status', label: 'Status' },
  { key: 'description', label: 'Description' },
];

const STATUS_VARIANT = { OPEN: 'warning', APPROVED: 'success', REJECTED: 'danger' };

export function TimeTrackingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const entries = useQuery({
    queryKey: ['projects', 'time-entries'],
    queryFn: () => projectsApi.listTimeEntries({ limit: 200 }).then((r) => r.items),
  });
  const byProject = useQuery({
    queryKey: ['projects', 'hours-by-project'],
    queryFn: () => projectsApi.hoursByProject().then((r) => r.items.projects),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => projectsApi.setTimeStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', 'time-entries'] }),
  });
  const remove = useMutation({
    mutationFn: (id) => projectsApi.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'hours-by-project'] });
    },
  });

  const rows = entries.data || [];
  const totalHours = rows.reduce((acc, r) => acc + (Number(r.hours) || 0), 0);
  const billableHours = rows.filter((r) => r.billable).reduce((acc, r) => acc + (Number(r.hours) || 0), 0);
  const openCount = rows.filter((r) => r.status === 'OPEN').length;

  const columns = [
    { key: 'entryDate', label: t('time.col.date'), render: (r) => formatDate(r.entryDate) },
    { key: 'employeeName', label: t('time.col.employee'), render: (r) => r.employeeName || '-' },
    {
      key: 'hours',
      label: t('time.col.hours'),
      align: 'right',
      render: (r) => formatNumber(r.hours, 2),
    },
    {
      key: 'billable',
      label: t('time.col.billable'),
      render: (r) => (r.billable ? <Badge variant="info">{t('time.yes')}</Badge> : <span className="muted">{t('time.no')}</span>),
    },
    {
      key: 'status',
      label: t('col.status'),
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`time.status.${r.status}`)}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          {r.status !== 'APPROVED' && (
            <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: r.id, status: 'APPROVED' })}>
              {t('time.action.approve')}
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => remove.mutate(r.id)}>
            {t('time.action.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('time.title')}</h1>
          <p className="muted">{t('time.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={rows} columns={CSV_COLUMNS} filename="zeiterfassung" />
          <Button variant="primary" onClick={() => setModalOpen(true)}>{t('time.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('time.metric.total')} value={`${formatNumber(totalHours, 1)} h`} />
        <MetricCard label={t('time.metric.billable')} value={`${formatNumber(billableHours, 1)} h`} trendVariant="success" />
        <MetricCard label={t('time.metric.open')} value={formatNumber(openCount, 0)} />
        <MetricCard label={t('time.metric.projects')} value={formatNumber((byProject.data || []).length, 0)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={entries.isLoading} emptyMessage={t('time.empty')} />

      <TimeEntryModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
